import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	TextField,
	Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UndoIcon from '@mui/icons-material/Undo';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import type { NoteEntry, NoteVariant } from '../types';
import NoteColorPickerButton from './NoteColorPickerButton';
import NoteVariantSelectButton from './NoteVariantSelectButton';
import { handleNoteTitleKeyDown } from './noteDialogUtils';

const AUTOSAVE_DEBOUNCE_MS = 1500;

export type EditNoteSavePayload = {
	id: string;
	title: string;
	variant: NoteVariant;
	color: string | null;
};

export interface EditNoteDialogProps {
	open: boolean;
	note: NoteEntry | null;
	onClose: () => void;
	onAutosave: (payload: EditNoteSavePayload) => Promise<void>;
	onSaveAndClose: (payload: EditNoteSavePayload) => Promise<void>;
}

interface NoteFormValues {
	title: string;
	variant: NoteVariant;
}

interface NoteFormSnapshot {
	title: string;
	variant: NoteVariant;
	color: string | null;
}

const noteToFormValues = (note: NoteEntry): NoteFormValues => ({
	title: note.title ?? '',
	variant: note.variant ?? 'default',
});

const snapshotFromNote = (note: NoteEntry): NoteFormSnapshot => ({
	title: note.title ?? '',
	variant: note.variant ?? 'default',
	color: note.color ?? null,
});

const snapshotFromState = (values: NoteFormValues, color: string | null): NoteFormSnapshot => ({
	title: values.title,
	variant: values.variant,
	color,
});

const snapshotsEqual = (a: NoteFormSnapshot, b: NoteFormSnapshot): boolean =>
	a.title === b.title && a.variant === b.variant && a.color === b.color;

const EditNoteDialog: React.FC<EditNoteDialogProps> = ({
	open,
	note,
	onClose,
	onAutosave,
	onSaveAndClose,
}) => {
	const [values, setValues] = useState<NoteFormValues>({
		title: '',
		variant: 'default',
	});
	const [color, setColor] = useState<string | null>(null);
	const [baseline, setBaseline] = useState<NoteFormSnapshot | null>(null);
	const [lastSynced, setLastSynced] = useState<NoteFormSnapshot | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [hasEditedSinceOpen, setHasEditedSinceOpen] = useState(false);

	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const saveGenerationRef = useRef(0);
	const openedNoteIdRef = useRef<string | null>(null);

	const currentSnapshot = useMemo(
		() => snapshotFromState(values, color),
		[values, color],
	);

	const isLongVariant = values.variant === 'long';
	const isDirty = baseline != null && lastSynced != null && !snapshotsEqual(currentSnapshot, lastSynced);
	const isAutosaveDirty = isLongVariant && isDirty;

	const buildSavePayload = useCallback((): EditNoteSavePayload | null => {
		if (!note?.id) {
			return null;
		}
		const title = values.title.trim();
		if (!title) {
			return null;
		}
		return {
			id: note.id,
			title,
			variant: values.variant,
			color,
		};
	}, [note?.id, values.title, values.variant, color]);

	const performSaveSnapshot = useCallback(
		async (snapshot: NoteFormSnapshot) => {
			if (snapshot.variant !== 'long' || !note?.id || !lastSynced) {
				return;
			}
			const title = snapshot.title.trim();
			if (!title || snapshotsEqual(snapshot, lastSynced)) {
				return;
			}

			const payload: EditNoteSavePayload = {
				id: note.id,
				title,
				variant: snapshot.variant,
				color: snapshot.color,
			};

			const generation = ++saveGenerationRef.current;
			setIsSaving(true);
			try {
				await onAutosave(payload);
				if (generation !== saveGenerationRef.current) {
					return;
				}
				setLastSynced(snapshot);
			} finally {
				if (generation === saveGenerationRef.current) {
					setIsSaving(false);
				}
			}
		},
		[note?.id, lastSynced, onAutosave],
	);

	const performSave = useCallback(async () => {
		if (values.variant !== 'long') {
			return;
		}
		await performSaveSnapshot(snapshotFromState(values, color));
	}, [color, performSaveSnapshot, values]);

	const clearDebounce = useCallback(() => {
		if (debounceTimerRef.current != null) {
			clearTimeout(debounceTimerRef.current);
			debounceTimerRef.current = null;
		}
	}, []);

	const scheduleAutosave = useCallback(() => {
		clearDebounce();
		debounceTimerRef.current = setTimeout(() => {
			debounceTimerRef.current = null;
			void performSave();
		}, AUTOSAVE_DEBOUNCE_MS);
	}, [clearDebounce, performSave]);

	useEffect(() => {
		if (!open) {
			openedNoteIdRef.current = null;
			return;
		}
		if (!note || openedNoteIdRef.current === note.id) {
			return;
		}
		openedNoteIdRef.current = note.id;
		const initial = snapshotFromNote(note);
		setValues(noteToFormValues(note));
		setColor(note.color ?? null);
		setBaseline(initial);
		setLastSynced(initial);
		setIsSaving(false);
		setHasEditedSinceOpen(false);
		saveGenerationRef.current += 1;
	}, [open, note]);

	useEffect(() => {
		if (!isLongVariant) {
			clearDebounce();
			return;
		}
		if (!open || !note || !isAutosaveDirty) {
			return;
		}
		if (!values.title.trim()) {
			return;
		}
		scheduleAutosave();
		return clearDebounce;
	}, [open, note, isAutosaveDirty, isLongVariant, values.title, values.variant, color, scheduleAutosave, clearDebounce]);

	useEffect(() => {
		if (!isLongVariant) {
			clearDebounce();
		}
	}, [isLongVariant, clearDebounce]);

	useEffect(() => {
		if (!open) {
			clearDebounce();
			saveGenerationRef.current += 1;
			setIsSaving(false);
		}
	}, [open, clearDebounce]);

	const handleChange = (patch: Partial<NoteFormValues>) => {
		const nextVariant = patch.variant ?? values.variant;
		if (nextVariant === 'long') {
			setHasEditedSinceOpen(true);
		}
		setValues((prev) => ({
			...prev,
			...patch,
		}));
	};

	const handleColorChange = (next: string | null) => {
		if (values.variant === 'long') {
			setHasEditedSinceOpen(true);
		}
		setColor(next);
	};

	const handlePromoteToLong = useCallback(() => {
		handleChange({ variant: 'long' });
	}, []);

	const handleTitleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			handleNoteTitleKeyDown(event, {
				isLong: values.variant === 'long',
				onPromoteToLong: handlePromoteToLong,
			});
		},
		[values.variant, handlePromoteToLong],
	);

	const canResetChanges = isLongVariant
		&& hasEditedSinceOpen
		&& baseline != null
		&& !snapshotsEqual(currentSnapshot, baseline);

	const handleResetChanges = () => {
		if (!baseline) {
			return;
		}
		clearDebounce();
		saveGenerationRef.current += 1;
		setValues({ title: baseline.title, variant: baseline.variant });
		setColor(baseline.color);
		setHasEditedSinceOpen(false);
		void performSaveSnapshot(baseline);
	};

	const flushSave = useCallback(async () => {
		clearDebounce();
		await performSave();
	}, [clearDebounce, performSave]);

	const handleSaveAndClose = async () => {
		const payload = buildSavePayload();
		if (!payload) {
			return;
		}
		if (isLongVariant) {
			await flushSave();
		}
		await onSaveAndClose(payload);
	};

	const syncStatusTooltip = isSaving
		? 'Saving…'
		: isAutosaveDirty
			? 'Unsaved changes'
			: 'All changes saved';

	const variantSelectSlot = (
		<NoteVariantSelectButton
			value={values.variant}
			onChange={(variant) => handleChange({ variant })}
		/>
	);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={values.variant === 'long' ? 'sm' : 'xs'}
			fullWidth
			slotProps={{
				paper: {
					sx: color
						? {
							bgcolor: (theme) => alpha(color, theme.palette.mode === 'dark' ? 0.22 : 0.14),
							transition: 'background-color 0.2s ease',
						}
						: undefined,
				},
			}}
			scroll='body'
		>
			<Box
				component='form'
				onSubmit={(event) => {
					event.preventDefault();
					void handleSaveAndClose();
				}}
				autoComplete='off'
				sx={{ display: 'contents' }}
			>
				<Box display="flex" justifyContent="flex-end" height={48} mb={-3} alignItems="center">
					<Box pr={2}>{variantSelectSlot}</Box>
				</Box>
				<DialogContent sx={{position: 'relative'}}>
					<Box display='flex' flexDirection='column' gap={2} mt={0}>
						<TextField
							onChange={(event) => handleChange({ title: event.target.value === ' ' ? '' : event.target.value })}
							value={values.title}
							variant='standard'
							fullWidth
							required
							autoFocus
							multiline={values.variant === 'long'}
							minRows={values.variant === 'long' ? 4 : undefined}
							placeholder='Add a note...'
							slotProps={{ input: { onKeyDown: handleTitleKeyDown } }}
						/>
					</Box>
					{isLongVariant && (<Box sx={{position:'absolute', right: 24, bottom: 0}}>
						<Tooltip title={syncStatusTooltip} disableInteractive>
							<Box
								component='span'
							>
								{(isSaving || isAutosaveDirty) ? null : (
									<DoneAllIcon fontSize='small' color='action' sx={{ fontSize: 14, color: 'primary.light' }}/>
								)}
							</Box>
						</Tooltip>
					</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between' }}>
					<NoteColorPickerButton value={color} onChange={handleColorChange} />
					<Box display='flex' gap={1} alignItems='center'>
						{isLongVariant && (
							<Button
								type='button'
								size='small'
								onClick={handleResetChanges}
								startIcon={<UndoIcon />}
								disabled={!canResetChanges}
							>
								Revert
							</Button>
						)}
						<Button
							type='submit'
							variant='contained'
							size='small'
							disabled={!values.title.trim()}
						>
							{isLongVariant ? 'Save & Close' : 'Update note'}
						</Button>
					</Box>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default EditNoteDialog;
